import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import styled from 'styled-components'
import GroupFilterSelector from './GroupFilterSelector.jsx'
import GroupFilterTagsContainer from './GroupFilterTagsContainer.jsx'
import useTranslation from '../../hooks/useTranslation.jsx'
import { getFromLS, saveToLS } from '../../utils/localStore/localStore'

const SelectorsRow = styled.div`
  display: inline-flex;
  gap: 0.5rem;
  align-items: center;
  flex-wrap: wrap;
`

/**
 * useGroupFilter
 * The selectors and the tags of a list's group filters, as two pieces that
 * share one state: the list's top bar puts the selectors beside the search
 * and the tags on a line of their own, shown only while something is picked.
 *
 * - listKey: string (unique per list, e.g., list type)
 * - config: { filters: Array<FilterConfig> } — absent or empty, both pieces are null
 * - onChange: (filterString: string) => void
 */
const useGroupFilter = ({ listKey, config, onChange }) => {
  const { t } = useTranslation()
  const enabled = Array.isArray(config?.filters) && config.filters.length > 0

  // Map of endpoint -> string[]
  const [selectedMap, setSelectedMap] = useState({})
  // Ordered tags: [{ id, selectorIndex, endpoint, label }]
  const [order, setOrder] = useState([])
  // Track whether localStorage load has finished
  const didLoadRef = useRef(false)

  const storageId = 'groupFilters'


  useEffect(() => {
    //console.log('[i] SelectedMap -> ', selectedMap)
    //console.log('[i] Order -> ', order)
  }, [selectedMap, order])

  // Load persisted state on mount or listKey change
  useEffect(() => {
    const persisted = getFromLS(storageId, listKey)
    if (persisted && typeof persisted === 'object') {
      setSelectedMap(persisted.map || {})
      setOrder(Array.isArray(persisted.order) ? persisted.order : [])
    } else {
      setSelectedMap({})
      setOrder([])
    }
  }, [listKey])

  // Mark as loaded once both selectedMap and order have been initialized
  useEffect(() => {
    if (!didLoadRef.current && selectedMap && order) {
      didLoadRef.current = true
    }
  }, [selectedMap, order])

  // Build filter string from selectedMap (supports primitives or [value, field] pairs)
  const filterString = useMemo(() => {
    const parts = []
    ; (config?.filters || []).forEach(cfg => {
      const endpointKey = cfg?.endpoint
      if (!endpointKey) return
      const defaultField = cfg?.filterField || endpointKey
      const vals = selectedMap[endpointKey]
      if (Array.isArray(vals) && vals.length > 0) {
        const byField = new Map()
        for (const entry of vals) {
          if (Array.isArray(entry)) {
            const v = String(entry[0])
            const f = String(entry[1] ?? defaultField)
            if (!byField.has(f)) byField.set(f, [])
            byField.get(f).push(v)
          } else {
            const v = String(entry)
            const f = String(defaultField)
            if (!byField.has(f)) byField.set(f, [])
            byField.get(f).push(v)
          }
        }
        for (const [field, arr] of byField.entries()) {
          if (arr.length > 0) parts.push(`${field}:${arr.join(',')}`)
        }
      }
    })
    return parts.join(';')
  }, [config, selectedMap])

  // Persist and notify parent when filter changes (skip until initial LS load done)
  useEffect(() => {
    if (!didLoadRef.current || !enabled) return
    saveToLS(storageId, listKey, { map: selectedMap, order })
    if (onChange) onChange(filterString || '')
  }, [enabled, filterString, listKey, onChange, order, selectedMap])

  const removeFromOrder = (endpoint, id) => {
    setOrder(prev => prev.filter(t => !(t.endpoint === endpoint && t.id === id)))
  }

  const addToOrder = (selectorIndex, endpoint, id, labelText) => {
    setOrder(prev => {
      if (prev.some(t => t.endpoint === endpoint && t.id === id)) return prev
      return [...prev, { selectorIndex, endpoint, id, label: labelText }]
    })
  }

  const handleRemoveTag = (id) => {
    const entry = order.find(t => t.id === id)
    if (!entry) return
    const { endpoint } = entry
    setSelectedMap(prev => {
      const next = { ...prev }
      const arr = Array.isArray(next[endpoint]) ? next[endpoint] : []
      next[endpoint] = arr.filter(x => Array.isArray(x) ? String(x[0]) !== String(id) : String(x) !== String(id))
      return next
    })
    removeFromOrder(endpoint, id)
  }

  const makeOnSelectorChange = useCallback((selectorIndex, cfg) => {
    const endpoint = cfg?.endpoint
    const labelText = cfg?.label ? (t ? t(cfg.label) : cfg.label) : ''
    const multiSelect = !!cfg?.multiSelect

    return (nextIds, ctx) => {
      setSelectedMap(prev => {
        const next = { ...prev }
        const before = Array.isArray(prev[endpoint]) ? prev[endpoint] : []

        if (!multiSelect) {
          before.forEach(oldId => removeFromOrder(endpoint, oldId))
          next[endpoint] = nextIds
        } else {
          next[endpoint] = nextIds
          const toggledId = ctx?.toggledId
          const added = !!ctx?.added
          if (toggledId) {
            if (added) {
              const primaryOnly = ctx?.item ? String(ctx.item[cfg?.displayField || 'id'] ?? toggledId) : toggledId
              const fullLabel = ctx?.displayLabel || (labelText ? `${labelText}: ${primaryOnly}` : `${primaryOnly}`)
              addToOrder(selectorIndex, endpoint, toggledId, fullLabel)
            } else {
              removeFromOrder(endpoint, toggledId)
            }
          }
        }

        if (!multiSelect) {
          const only = nextIds[0]
          if (only) {
            const primaryOnly = ctx?.item ? String(ctx.item[cfg?.displayField || 'id'] ?? only) : only
            const fullLabel = ctx?.displayLabel || (labelText ? `${labelText}: ${primaryOnly}` : `${primaryOnly}`)
            addToOrder(selectorIndex, endpoint, only, fullLabel)
          }
        }

        return next
      })
    }
  }, [t])

  // Cleanup: ensure order matches selectedMap (only after initial load and valid data)
  useEffect(() => {
    if (!didLoadRef.current) return
    if (!selectedMap || Object.keys(selectedMap).length === 0) return
    setOrder(prev =>
      prev.filter(tag => {
        const arr = selectedMap[tag.endpoint]
        if (!Array.isArray(arr)) return false
        return arr.some(x => Array.isArray(x) ? String(x[0]) === String(tag.id) : String(x) === String(tag.id))
      })
    )
  }, [selectedMap])


  if (!enabled) return { selectors: null, tags: null }

  return {
    selectors: (
      <SelectorsRow>
        {config.filters.map((cfg, idx) => (
          <GroupFilterSelector
            key={`${cfg.endpoint}-${idx}`}
            config={cfg}
            value={selectedMap[cfg.endpoint] || []}
            onChange={makeOnSelectorChange(idx, cfg)}
          />
        ))}
      </SelectorsRow>
    ),
    tags: order.length > 0
      ? <GroupFilterTagsContainer tags={order} onRemove={handleRemoveTag} />
      : null,
  }
}

export default useGroupFilter
